/**
 * Keeps the feed from moving under the reader.
 *
 * The feed refreshes in the background (a cache restore on load, a cursor
 * that expired, the one-minute head check), and every refresh can bring a
 * different ranking. Applying that ranking as-is would shuffle the posts the
 * reader is looking at, so this decides what is actually displayed:
 *
 * - Posts already shown ("seen") keep their place while the reader is scrolled
 *   down. At the top of the feed they quietly take the fresh order instead,
 *   which is how boosts and re-ranking land without a jump.
 * - Unseen posts ranked above the last seen one are held back as `pending`
 *   (the "N new posts" pill) unless `isDirect` says to show them at once (the
 *   reader's own posts).
 * - Unseen posts ranked below everything seen are just the next page, so they
 *   are appended.
 * - `acceptAll` (the pill was tapped) shows the fresh order in full.
 */
export type StableFeedState = Readonly<{ order: string[]; seen: string[] }>;

export type ReconcileInput = Readonly<{
  /** Ids from the loaded list pages, in ranked order. */
  freshIds: readonly string[];
  /** Ids from the latest head (page 1) check, in ranked order. */
  headIds?: readonly string[];
  atTop: boolean;
  acceptAll?: boolean;
  isDirect?: (id: string) => boolean;
}>;

export type ReconcileResult = Readonly<{
  state: StableFeedState;
  pending: string[];
}>;

const unique = (ids: readonly string[]) => [...new Set(ids)];

export function reconcileFeed(
  prev: StableFeedState | null,
  input: ReconcileInput,
): ReconcileResult {
  const fresh = unique(input.freshIds);
  const isDirect = input.isDirect ?? (() => false);

  if (!prev || input.acceptAll) {
    const head = (input.headIds ?? []).filter((id) => !fresh.includes(id));
    // Anything only the head knows about stays pending until the list itself
    // has it (the caller refetches before accepting).
    return {
      state: { order: fresh, seen: unique([...(prev?.seen ?? []), ...fresh]) },
      pending: input.acceptAll ? [] : head.filter((id) => !isDirect(id)),
    };
  }

  const seen = new Set(prev.seen);
  let lastSeenIndex = -1;
  fresh.forEach((id, index) => {
    if (seen.has(id)) lastSeenIndex = index;
  });

  const shown = new Set<string>(seen);
  const pending: string[] = [];
  fresh.forEach((id, index) => {
    if (seen.has(id)) return;
    if (index > lastSeenIndex || isDirect(id)) shown.add(id);
    else pending.push(id);
  });
  for (const id of input.headIds ?? []) {
    if (!shown.has(id) && !pending.includes(id)) {
      if (isDirect(id)) continue; // shows once the list has it
      pending.push(id);
    }
  }

  let order: string[];
  if (input.atTop) {
    // Quiet re-sort to the fresh ranking; posts that left the list go too.
    order = fresh.filter((id) => shown.has(id));
  } else {
    // Hold what is on screen; new arrivals go where they cannot push it:
    // the reader's own posts in front, the next page behind.
    const kept = prev.order.filter((id) => fresh.includes(id) || seen.has(id));
    const keptSet = new Set(kept);
    const added = fresh.filter((id) => shown.has(id) && !keptSet.has(id));
    const front = added.filter((id) => !seen.has(id) && isDirect(id));
    const back = added.filter((id) => !front.includes(id));
    order = [...front, ...kept, ...back];
  }

  return {
    state: { order, seen: unique([...prev.seen, ...order]) },
    pending,
  };
}

/** Drops a post from the displayed order (after deleting it). */
export function forgetPost(state: StableFeedState, id: string): StableFeedState {
  return {
    order: state.order.filter((x) => x !== id),
    seen: state.seen.filter((x) => x !== id),
  };
}
